import { useMutation, useQuery } from "@apollo/client";
import {
	Anchor,
	Badge,
	Button,
	Card,
	Container,
	Group,
	Loader,
	Modal,
	Pagination,
	ScrollArea,
	Stack,
	Table,
	Text,
	TextInput,
	Title,
	Tooltip,
} from "@mantine/core";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";
import {
	IconSearch,
	IconTrash,
	IconUser,
	IconUsers,
} from "@tabler/icons-react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import type { EmployeeGetUsersQuery } from "../../../graphql/generated/types.gen";
import {
	EMPLOYEE_DELETE_USER,
	EMPLOYEE_GET_USERS,
} from "../../../graphql/queries";
import { useAuthCompat } from "../../../hooks/useAuthCompat";
import { useIsMobile } from "../../../hooks/useIsMobile";

export const Route = createFileRoute("/admin/users/")({
	component: AdminUsersPage,
});

const PAGE_SIZE = 20;

function AdminUsersPage() {
	const { user, isLoading: authLoading } = useAuthCompat();
	const isMobile = useIsMobile();

	const [search, setSearch] = useState("");
	const [debouncedSearch] = useDebouncedValue(search, 300);
	const [page, setPage] = useState(1);

	const [
		deleteModalOpened,
		{ open: openDeleteModal, close: closeDeleteModal },
	] = useDisclosure(false);
	const [userToDelete, setUserToDelete] = useState<{
		id: string;
		displayName: string;
	} | null>(null);

	useEffect(() => {
		if (authLoading) return;
		if (!user) {
			window.location.href = "/welcome";
			return;
		}
		if (!user.isEmployee) {
			window.location.href = "/";
		}
	}, [user, authLoading]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: Reset page when search changes
	useEffect(() => {
		setPage(1);
	}, [debouncedSearch]);

	const { data, loading, refetch } = useQuery<EmployeeGetUsersQuery>(
		EMPLOYEE_GET_USERS,
		{
			variables: {
				search: debouncedSearch || undefined,
				limit: PAGE_SIZE,
				offset: (page - 1) * PAGE_SIZE,
			},
			skip: !user?.isEmployee,
		},
	);

	const [deleteUser, { loading: deleting }] = useMutation(
		EMPLOYEE_DELETE_USER,
		{
			onCompleted: () => {
				closeDeleteModal();
				setUserToDelete(null);
				// If this was the last item on the page, go back one page
				if (users.length === 1 && page > 1) {
					setPage(page - 1);
				} else {
					refetch();
				}
			},
			onError: (error) => {
				alert(`Failed to delete user: ${error.message}`);
			},
		},
	);

	const handleDeleteClick = (u: { id: string; displayName: string }) => {
		setUserToDelete(u);
		openDeleteModal();
	};

	const confirmDelete = () => {
		if (userToDelete) {
			deleteUser({ variables: { userId: userToDelete.id } });
		}
	};

	if (authLoading || !user?.isEmployee) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<Loader size="lg" />
			</div>
		);
	}

	const users = data?.employeeUsers.users || [];
	const totalCount = data?.employeeUsers.totalCount || 0;
	const totalPages = Math.ceil(totalCount / PAGE_SIZE);

	return (
		<div className="h-[calc(100vh-var(--header-height))] overflow-auto bg-[var(--mantine-color-body)]">
			<Container size="lg" py="xl" px={isMobile ? "md" : "xl"}>
				<Group gap="md" mb="xs" wrap="nowrap">
					<IconUsers
						size={isMobile ? 24 : 32}
						color="var(--mantine-color-blue-6)"
					/>
					<Title order={isMobile ? 2 : 1}>Users</Title>
				</Group>
				<div className="mb-6">
					<Anchor href="/admin">&larr; Back to Admin</Anchor>
				</div>

				<TextInput
					placeholder="Search by name or email..."
					leftSection={<IconSearch size={16} />}
					value={search}
					onChange={(e) => setSearch(e.currentTarget.value)}
					mb="md"
				/>

				{loading ? (
					<div className="flex items-center justify-center py-8">
						<Loader size="lg" />
					</div>
				) : users.length === 0 ? (
					<Card shadow="sm" padding="xl" radius="md" withBorder>
						<Text size="lg" c="dimmed" ta="center">
							{debouncedSearch ? "No users found." : "No users yet."}
						</Text>
					</Card>
				) : isMobile ? (
					<Stack gap="md">
						{users.map((u) => (
							<Card key={u.id} shadow="sm" padding="md" radius="md" withBorder>
								<Stack gap="xs">
									<Group justify="space-between" align="flex-start">
										<Link
											to="/admin/users/$userId"
											params={{ userId: u.id }}
											style={{ textDecoration: "none", color: "inherit" }}
										>
											<Text fw={600}>{u.displayName}</Text>
										</Link>
										{u.isEmployee && (
											<Badge color="violet" size="sm">
												Employee
											</Badge>
										)}
									</Group>
									<Text size="sm" c="dimmed" style={{ wordBreak: "break-all" }}>
										{u.email}
									</Text>
									<Group justify="space-between" align="center">
										<Badge
											color={
												u.betaAccessStatus === "APPROVED" ? "green" : "yellow"
											}
											size="sm"
										>
											{u.betaAccessStatus}
										</Badge>
										<Text size="xs" c="dimmed">
											{u.memberships.length} org
											{u.memberships.length !== 1 && "s"}
										</Text>
									</Group>
									<Group justify="space-between" align="center">
										<Tooltip label={new Date(u.createdAt).toLocaleString()}>
											<Text size="xs" c="dimmed" style={{ cursor: "default" }}>
												{new Date(u.createdAt).toLocaleDateString()}
											</Text>
										</Tooltip>
										<Button
											size="xs"
											color="red"
											variant="light"
											leftSection={<IconTrash size={14} />}
											onClick={() => handleDeleteClick(u)}
										>
											Delete
										</Button>
									</Group>
								</Stack>
							</Card>
						))}
					</Stack>
				) : (
					<Card shadow="sm" padding={0} radius="md" withBorder>
						<ScrollArea>
							<Table striped highlightOnHover>
								<Table.Thead>
									<Table.Tr>
										<Table.Th>Name</Table.Th>
										<Table.Th>Email</Table.Th>
										<Table.Th>Status</Table.Th>
										<Table.Th>Employee</Table.Th>
										<Table.Th>Orgs</Table.Th>
										<Table.Th>Created</Table.Th>
										<Table.Th>Actions</Table.Th>
									</Table.Tr>
								</Table.Thead>
								<Table.Tbody>
									{users.map((u) => (
										<Table.Tr key={u.id}>
											<Table.Td>
												<Link
													to="/admin/users/$userId"
													params={{ userId: u.id }}
													style={{ textDecoration: "none" }}
												>
													<Group gap="xs" wrap="nowrap">
														<IconUser size={16} />
														<Text size="sm">{u.displayName}</Text>
													</Group>
												</Link>
											</Table.Td>
											<Table.Td>{u.email}</Table.Td>
											<Table.Td>
												<Badge
													color={
														u.betaAccessStatus === "APPROVED"
															? "green"
															: "yellow"
													}
													size="sm"
												>
													{u.betaAccessStatus}
												</Badge>
											</Table.Td>
											<Table.Td>
												{u.isEmployee && (
													<Badge color="violet" size="sm">
														Employee
													</Badge>
												)}
											</Table.Td>
											<Table.Td>{u.memberships.length}</Table.Td>
											<Table.Td>
												<Tooltip label={new Date(u.createdAt).toLocaleString()}>
													<Text size="sm" style={{ cursor: "default" }}>
														{new Date(u.createdAt).toLocaleDateString()}
													</Text>
												</Tooltip>
											</Table.Td>
											<Table.Td>
												<Button
													size="xs"
													color="red"
													variant="light"
													leftSection={<IconTrash size={14} />}
													onClick={() => handleDeleteClick(u)}
												>
													Delete
												</Button>
											</Table.Td>
										</Table.Tr>
									))}
								</Table.Tbody>
							</Table>
						</ScrollArea>
					</Card>
				)}

				{totalPages > 1 && (
					<Group justify="center" mt="md">
						<Pagination
							value={page}
							onChange={setPage}
							total={totalPages}
							size={isMobile ? "sm" : "md"}
						/>
						<Text size="sm" c="dimmed">
							{totalCount} user{totalCount !== 1 && "s"} total
						</Text>
					</Group>
				)}
			</Container>

			<Modal
				opened={deleteModalOpened}
				onClose={closeDeleteModal}
				title="Delete User"
				centered
			>
				<Stack>
					<Text>
						Are you sure you want to delete{" "}
						<strong>{userToDelete?.displayName}</strong>?
					</Text>
					<Text size="sm" c="dimmed">
						This will remove the user and all their organization memberships.
						This action cannot be undone.
					</Text>
					<Group justify="flex-end" mt="md">
						<Button variant="default" onClick={closeDeleteModal}>
							Cancel
						</Button>
						<Button color="red" onClick={confirmDelete} loading={deleting}>
							Delete
						</Button>
					</Group>
				</Stack>
			</Modal>
		</div>
	);
}
